import React, { useState } from 'react';
import { ArchiveIcon, DownloadIcon } from '@radix-ui/react-icons';
import { ElevatedButton, IconButton, Tooltip } from '@producer/ui-kit';

import { SliderInput } from '../../../components/slider-input/slider-input';
import { ToolBar, Tools } from '../../../components/toolbar/toolbar';
import { Storyboard } from '../../../app/entities';
import { BufferToBlob } from '../../../lib/utils/BufferToBolb';
import { createFileName } from '../../../core/services/file.service';
import { LimitReachedModal } from '../../../components/dialogs/limit-reached.modal';
import { useStoryboard } from '../hooks/useStoryboard';
import { useCurrentMember } from '../../../hooks/use-current-member';
import { uiStore } from '../../../core/stores/ui-store';
import { ControlHubIcon } from '../../../components/icons';

interface StoryboardToolbarProps {
  storyboard: Storyboard;
  toggleBoneyard: () => void;
}

export const StoryboardToolbar: React.FC<StoryboardToolbarProps> = ({
  storyboard,
  toggleBoneyard,
}) => {
  const currentMember = useCurrentMember();

  const storyboardPreferences = currentMember?.preferences?.storyBoardPreferences;

  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [showLimitReachedModal, setShowLimitReachedModal] = useState(false);
  const { fetchStoryboardAsPDF } = useStoryboard(storyboard.project!._id);

  const handleDownload = async () => {
    if (generatingPDF || !storyboard) {
      return;
    }

    if (!storyboard.project?.space?.canExport()) {
      return setShowLimitReachedModal(true);
    }

    setGeneratingPDF(true);

    try {
      const file = await fetchStoryboardAsPDF(storyboard._id);

      BufferToBlob(
        file,
        createFileName(storyboard.project.name, storyboard.name || 'Storyboard', 'pdf'),
      );
    } catch (e) {
      console.log(e);
    }

    setGeneratingPDF(false);
  };

  const handleZoomLevel = (value: number) => {
    if (!storyboard) return;
    currentMember?.changeZoomLevel(value);
  };

  return (
    <>
      <ToolBar>
        <div />
        <Tools>
          <p>Total shots: {storyboard?.shots.length}</p>

          <div key="zoom-input">
            <SliderInput
              min={1}
              max={6}
              step={1}
              value={storyboardPreferences?.zoom || 5}
              onChange={handleZoomLevel}
            />
          </div>

          <Tooltip text="Shot archive" position="bottom">
            <IconButton icon={<ArchiveIcon />} onClick={toggleBoneyard} />
          </Tooltip>

          <Tooltip text="Properties" position="bottom">
            <IconButton
              data-testid="customize-props-button"
              icon={<ControlHubIcon />}
              onClick={() =>
                uiStore.isPropertiesPanelOpen
                  ? uiStore.closePropertiesPanel()
                  : uiStore.openPropertiesPanel('panel')
              }
            />
          </Tooltip>

          <ElevatedButton
            key="download"
            onClick={handleDownload}
            isLoading={generatingPDF}
            disabled={!storyboard?.shots?.length}
            text="Generate PDF"
            icon={<DownloadIcon />}
          />
        </Tools>
      </ToolBar>

      {showLimitReachedModal && (
        <LimitReachedModal
          title="Upgrade your space to export your storyboard as a PDF"
          eventName="Export storyboard attempt"
          onCancel={() => setShowLimitReachedModal(false)}
        />
      )}
    </>
  );
};
